<template>
    <article
        :id="props.game?.hru"
        :class="$style.card"
        :data-interface-target="`game-number-${props.rank}`"
    >
        <div :class="$style.cover">
            <CardContainer
                :is="props.game ? BaseLink : 'div'"
                ref="cover"
                v-bind="props.link"
                :src="coverImage?.src"
                :srcset="coverImage?.srcset"
                :sizes="coverImage?.sizes"
                :expand="props.coverExpand"
                :alt="props.game?.title"
                :placeholder="!props.game"
                responsive
            />
            <span
                :class="[$style.rank_cover, props.rank === 1 && $style.rank_first]"
                aria-hidden="true"
            >
                {{ props.rank }}
            </span>
        </div>
        <div :class="$style.head">
            <div :class="$style.icon_wrap">
                <CardContainer
                    appearance="icon"
                    :src="props.game?.icon"
                    :placeholder="!props.game"
                    alt=""
                    :class="$style.icon_override"
                />
                <span
                    :class="$style.rank_icon"
                    aria-hidden="true"
                >
                    {{ props.rank }}
                </span>
            </div>
            <template v-if="props.game">
                <div :class="$style.title_block">
                    <Typography
                        is="h2"
                        type="header"
                        size="xs"
                        :responsive="false"
                        :class="$style.title_override"
                    >
                        {{ props.game.title }}
                    </Typography>
                    <Typography
                        v-if="props.game.mainCategory"
                        type="caption"
                        size="all-caps"
                        semibold
                        :responsive="false"
                        :class="$style.category_override"
                    >
                        {{ props.game.mainCategory }}
                    </Typography>
                </div>
                <div
                    :class="$style.likes"
                    :aria-label="$tr('gameContainer.like')"
                >
                    <Icon name="like" />
                    <Typography
                        type="label"
                        size="m"
                        accent
                        :responsive="false"
                    >
                        {{ formatValue(props.game.rating.count, { shorten: true }) }}
                    </Typography>
                </div>
            </template>
            <template v-else>
                <div :class="$style.title_block">
                    <AnimatedPlaceholder :class="$style.title_placeholder" />
                    <AnimatedPlaceholder :class="$style.category_placeholder" />
                </div>
                <AnimatedPlaceholder :class="$style.likes_placeholder" />
            </template>
        </div>
        <template v-if="props.game">
            <Button
                ref="play"
                v-bind="props.link"
                color="purple-400"
                width="full"
                size="xl"
                interfaceTarget="collection-play-now"
                :label="$tr('gameContainer.play')"
                :class="$style.play_override"
            />
            <div :class="$style.details">
                <Stars :rating="props.game.rating" />
                <Typography
                    is="p"
                    type="paragraph"
                    size="s"
                    :responsive="false"
                    :class="$style.meta_override"
                >
                    <span>{{ platformsText }}</span>
                    <span :class="$style.separator">/</span>
                    <span>{{ updatedText }}</span>
                    <template v-if="props.game.developer">
                        <span :class="$style.separator">/</span>
                        <Link
                            v-if="routes.developer"
                            color="white"
                            :to="routes.developer"
                        >
                            {{ props.game.developer.name }}
                        </Link>
                        <span v-else>{{ props.game.developer.name }}</span>
                    </template>
                </Typography>
                <Typography
                    is="p"
                    v-if="props.game.series.length"
                    type="paragraph"
                    size="s"
                    :responsive="false"
                >
                    <template
                        v-for="(series, index) in routes.series"
                        :key="series.hru"
                    >
                        <span v-if="index > 0">, </span>
                        <Link
                            color="purple-600"
                            :to="series.to"
                        >
                            {{ series.title }}
                        </Link>
                    </template>
                </Typography>
            </div>
            <div
                v-if="props.game.tags.length"
                :class="$style.tags"
            >
                <component
                    :is="index > 5 ? SeoOnly : 'div'"
                    v-for="(tag, index) in routes.tags"
                    :key="tag.hru"
                >
                    <TagButton
                        :to="tag.to"
                        :name="tag.title"
                        :count="tag.totalCount"
                        :class="$style.tag_override"
                    />
                </component>
            </div>
            <Typography
                is="p"
                type="paragraph"
                size="s"
                :responsive="false"
                :class="$style.description_override"
            >
                {{ props.game.description }}
            </Typography>
        </template>
        <template v-else>
            <AnimatedPlaceholder :class="$style.play_placeholder" />
            <div :class="$style.details">
                <AnimatedPlaceholder
                    v-for="index in 4"
                    :key="index"
                    :class="$style.line_placeholder"
                />
            </div>
        </template>
    </article>
</template>
<script setup lang="ts">
import { computed, useTemplateRef } from 'vue'
import type { RouteLocationRaw } from 'vue-router'

import AnimatedPlaceholder from '@/components/AnimatedPlaceholder/AnimatedPlaceholder.vue'
import BaseLink from '@/components/BaseLink.vue'
import Button from '@/components/Buttons/Button.vue'
import CardContainer from '@/components/CardContainer.vue'
import Icon from '@/components/Icon/Icon.vue'
import Link from '@/components/Link.vue'
import SeoOnly from '@/components/SeoOnly.vue'
import Stars from '@/components/Stars.vue'
import Typography from '@/components/Typography.vue'
import { useUrl } from '@/composables/url-helpers'
import { type CtrContext, useCtrTarget } from '@/composables/use-ctr-context'
import { useTr } from '@/composables/use-tr'
import { useI18nAddon } from '@/i18n/i18n'
import TagButton from '@/pages/Games/components/GameDetails/TagButton.vue'
import { formatValue } from '@/utils/numbers'

import { previewImage } from '../preview-image'
import type { CollectionGame } from '../use-collection-page-data'

const props = defineProps<{
    game?: CollectionGame
    rank: number
    link?: { to?: RouteLocationRaw; externalTo?: string }
    coverExpand?: number
    ctrContext?: CtrContext
}>()

const tr = useTr()
const { currentLocale } = useI18nAddon()
const { getCategoryRoute } = useUrl()

const platformsText = computed(() =>
    props.game?.platforms
        .map((platform) => {
            switch (platform) {
                case 'ios':
                    return tr('gameContainer.ios')
                case 'android':
                    return tr('gameContainer.android')
                default:
                    return tr('gameContainer.desktop')
            }
        })
        .join(', '),
)

// UTC so server and client agree on the month
const updatedText = computed(
    () =>
        props.game &&
        new Date(props.game.updatedAt).toLocaleDateString(currentLocale.value, {
            month: 'long',
            year: 'numeric',
            timeZone: 'UTC',
        }),
)

const routes = computed(() => ({
    developer: props.game?.developer?.id && getCategoryRoute({ hru: props.game.developer.id, type: 'developer' }),
    series:
        props.game?.series.map((series) => ({
            ...series,
            to: getCategoryRoute({ hru: series.hru, type: 'series' }),
        })) ?? [],
    tags: props.game?.tags.map((tag) => ({ ...tag, to: getCategoryRoute({ hru: tag.hru, type: 'tag' }) })) ?? [],
}))

const coverImage = computed(() => props.game && previewImage(props.game.preview))

const cover = useTemplateRef('cover')
const play = useTemplateRef('play')
const ctrContext = () => (props.game && props.ctrContext ? { target: props.game.hru, ...props.ctrContext } : undefined)

useCtrTarget(() => cover.value?.$el, ctrContext)
useCtrTarget(() => play.value?.$el, ctrContext, { skipImpression: true })
</script>
<style module>
.card {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    row-gap: 16px;
    padding: 16px 0;
    --mobile-header-height: 66px;
    scroll-margin-top: calc(var(--pg-safe-area-top) + var(--mobile-header-height));
}

.cover {
    position: relative;
    order: 1;
    margin-top: -4px;
}

.rank_cover {
    display: none;
}

.head {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
}

.icon_wrap {
    position: relative;
    flex-shrink: 0;
}

.icon_override {
    width: 56px;
}

.rank_icon {
    position: absolute;
    top: -5px;
    right: -5px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: var(--white);
    color: var(--black);
    font-size: 11rem;
    font-weight: var(--font-weight-accent);
    line-height: 1;
}

.title_block {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.title_override {
    /* room for descenders, which overflow: hidden clips at this line-height */
    margin-block: -0.2em;
    padding-block: 0.2em;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    line-height: 1.1;
}

.category_override {
    color: var(--white-60);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.likes {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 4px;
    box-sizing: border-box;
    height: 48px;
    padding: 0 12px 0 10px;
    border: 1px solid var(--white-08);
    border-radius: 40px;
    background: var(--carbon-300);
}

.likes_placeholder {
    flex-shrink: 0;
    width: 80px;
    height: 48px;
    border-radius: 40px;
}

.title_placeholder {
    width: 60%;
    height: 18px;
    border-radius: 9px;
}

.category_placeholder {
    width: 30%;
    height: 10px;
    border-radius: 5px;
}

.play_placeholder {
    order: 2;
    height: 56px;
    border-radius: 28px;
}

.line_placeholder {
    height: 12px;
    border-radius: 6px;
}

.line_placeholder:last-child {
    width: 60%;
}

.play_override {
    order: 2;
    height: 56px;
}

.details {
    order: 3;
    display: flex;
    flex-direction: column;
    gap: 6px;
}

/* flex gap instead of template whitespace, which Vue drops between tags */
.meta_override {
    display: flex;
    flex-wrap: wrap;
    column-gap: 4px;
}

.separator {
    color: var(--white-40);
}

.tags {
    order: 4;
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
}

.tag_override button {
    height: 24px;
    padding: 0 12px;
    font-size: var(--font-size-label-xs);
}

.tag_override button > * {
    font-size: inherit;
}

.description_override {
    order: 5;
    color: var(--carbon-600);
}

@media (width >= 640px) {
    .cover {
        order: 0;
        margin-top: 0;
    }

    .rank_cover {
        position: absolute;
        top: 4px;
        right: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 44px;
        height: 44px;
        border-radius: 50%;
        background: var(--lavander-600);
        color: var(--black);
        font-size: var(--font-size-header-xs);
        font-weight: var(--font-weight-accent);
        line-height: 1;
    }

    .rank_first {
        background: var(--golden-400);
    }

    .rank_icon {
        display: none;
    }

    .card {
        grid-template-columns: 24px minmax(0, 1fr) 24px;
    }

    .card > * {
        grid-column: 2;
    }

    .card > .cover {
        grid-column: 1 / -1;
    }

    .head {
        order: 1;
    }

    .category_override {
        color: var(--white-40);
        font-weight: var(--font-weight-accent);
    }

    .likes {
        height: 56px;
        padding: 0 18px 0 16px;
    }

    .likes_placeholder {
        height: 56px;
    }
}
</style>
